import {
  IDatabaseTableModel,
  IGenericSelectDataField,
  IGenericSelectDataModel,
  IGenericSelectRelationsModel,
  IGenericSelectTablesModel,
  IGenericSelectWhereDataModel
} from "@kuika/kuika-cl-model";
import { EManagedDbUpdateWhereType } from "@kuika/kuika-cl-model/src/models/builder.dtos";
import _ from "lodash";
import * as React from "react";
import { DeleteModal } from "../../../../common/delete-modal";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaMenuItem from "../../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";

export interface ITableListState {
  areasToDelete: IAreasToDelete;
  isDeleteModalVisible: boolean;
  tableToDelete: string | null;
}

export interface ITableListProps {
  genericSelectData: IGenericSelectDataModel;
  tables: IDatabaseTableModel[];
  handleAddJoinClick: (tableName: string, tableSchema: string) => void;
  handleDelete: (newData: IGenericSelectDataModel) => void;
  getTypeIcon: (type: string) => React.ReactElement;
  onSelectSourceClick: () => void;
  expandedKeys: string[];
  onExpandedKeysChange: (keys: string[]) => void;
}

export interface IAreasToDelete {
  tables: ITableInLoop[];
  relations: IRelationInLoop[];
}

interface ITableInLoop extends IGenericSelectTablesModel {
  areaIdx?: number;
}

interface IRelationInLoop extends IGenericSelectRelationsModel {
  areaIdx?: number;
}

class TableList extends React.PureComponent<ITableListProps, ITableListState> {
  constructor(props: ITableListProps) {
    super(props);
    this.state = {
      areasToDelete: {
        tables: [],
        relations: []
      },
      isDeleteModalVisible: false,
      tableToDelete: null
    };
  }

  getTableType = (tableName: string): "parent" | "origin" | "child" | null => {
    const { genericSelectData } = this.props;
    const tables = genericSelectData.tables;
    const relations = genericSelectData.relations;

    // Check if this is the origin table (first table)
    if (tables.length > 0 && tables[0].name === tableName) {
      return "origin";
    }

    // Check if this table is a parent (appears in toTable of any relation)
    const isParent = relations.some((rel) => rel.toTable === tableName);
    if (isParent) {
      return "parent";
    }

    // Check if this table is a child (appears in fromTable of any relation, but not as origin)
    const isChild = relations.some((rel) => rel.fromTable === tableName);
    if (isChild) {
      return "child";
    }

    return null;
  };

  getTableBackgroundColor = (tableName: string): string | undefined => {
    const tableType = this.getTableType(tableName);
    switch (tableType) {
      case "parent":
        return "var(--purple-1)";
      case "origin":
        return "var(--blue-05)";
      case "child":
        return "var(--orange-1)";
      default:
        return undefined;
    }
  };

  handleDeleteTable = (tableSchema: string, tableName: string, tableAlias?: string) => {
    const areas = this.calculateAreas(tableSchema, tableName, tableAlias);
    this.setState({ isDeleteModalVisible: true, tableToDelete: tableName, areasToDelete: areas });
  };

  calculateAreas = (tableSchema: string, tableName: string, tableAlias?: string): IAreasToDelete => {
    const selectData = _.cloneDeep(this.props.genericSelectData);
    // Filter by alias if exists, otherwise by name
    const tables: ITableInLoop[] = selectData.tables.filter((x) => {
      if (tableAlias) {
        return x.alias !== tableAlias;
      }
      return x.name !== tableName;
    });
    const relations: IRelationInLoop[] = selectData.relations.filter(
      (x) =>
        !(
          (x.fromTable === tableName && x.fromTableSchema == tableSchema) ||
          (x.toTable === tableName && x.toTableSchema == tableSchema)
        )
    );

    let loopTable = tables[0];
    let areaIdx = 0;
    while (loopTable !== undefined) {
      loopTable.areaIdx = areaIdx;

      const fromRelations = relations.filter(
        (x) => x.areaIdx === undefined && x.fromTable === loopTable!.name && x.fromTableSchema === loopTable?.schema
      );
      const toRelations = relations.filter(
        (x) => x.areaIdx === undefined && x.toTable === loopTable!.name && x.toTableSchema === loopTable?.schema
      );

      fromRelations.forEach((relation) => {
        relation.areaIdx = areaIdx;
      });

      toRelations.forEach((relation) => {
        relation.areaIdx = areaIdx;
      });

      const relationsInArea = relations.filter((x) => x.areaIdx === areaIdx);
      const relationTableNamesInArea = relationsInArea
        .map((x) => x.fromTable)
        .concat(relationsInArea.map((x) => x.toTable));
      const notUsedTablesInArea = tables.filter(
        (x) => x.areaIdx === undefined && relationTableNamesInArea.indexOf(x.name) > -1
      );
      const nextLoopTable = notUsedTablesInArea[0];
      if (nextLoopTable) {
        loopTable = nextLoopTable;
      } else {
        areaIdx += 1;
        loopTable = tables.filter((x) => x.areaIdx === undefined)[0];
      }
    }

    const result: IAreasToDelete = {
      tables,
      relations
    };
    return result;
  };

  handleExecuteDelete = () => {
    const genericSelectData = _.cloneDeep(this.props.genericSelectData);
    const { areasToDelete } = this.state;

    // Only keep tables and relations that are in the same area as origin (areaIdx === 0)
    // Tables/relations in different areas are disconnected from origin and should be removed
    const tables = areasToDelete.tables
      .filter((t) => t.areaIdx === 0)
      .map((t) => {
        const { areaIdx, ...rest } = t;
        return rest;
      });
    const relations = areasToDelete.relations
      .filter((r) => r.areaIdx === 0)
      .map((r) => {
        const { areaIdx, ...rest } = r;
        return rest;
      });

    const tableNames = tables.map((x) => x.name);

    // Filter calculated fields first - only keep if ALL referenced tables still exist
    const calculatedFields = (genericSelectData.calculatedFields || []).filter((calcField) => {
      // Check if any field in the calculated field references a deleted table
      const fieldsUseDeletedTable = (calcField.fields || []).some(
        (f) => f.tableName && f.tableName.trim() !== "" && tableNames.indexOf(f.tableName) === -1
      );
      const partitionByUseDeletedTable = (calcField.partitionByFields || []).some(
        (f) => f.tableName && f.tableName.trim() !== "" && tableNames.indexOf(f.tableName) === -1
      );
      const orderByUseDeletedTable = (calcField.orderByFields || []).some(
        (f) => f.tableName && f.tableName.trim() !== "" && tableNames.indexOf(f.tableName) === -1
      );

      // Keep the calculated field only if it doesn't use any deleted table
      return !fieldsUseDeletedTable && !partitionByUseDeletedTable && !orderByUseDeletedTable;
    });

    // Get aliases of remaining calculated fields
    const calculatedFieldAliases = calculatedFields
      .map((cf) => cf.alias)
      .filter((alias) => alias && alias.trim() !== "");

    // Special table key used for calculated fields in sort order
    const CALCULATED_FIELDS_TABLE_KEY = "__calculated_fields__";

    // Helper function to check if a field/condition should be kept
    const shouldKeepField = (tableName: string | undefined, colName: string | undefined): boolean => {
      // If it's a calculated field reference (tableName is the special key), check if the calculated field still exists
      if (tableName === CALCULATED_FIELDS_TABLE_KEY) {
        return colName ? calculatedFieldAliases.indexOf(colName) > -1 : false;
      }
      // If tableName exists and is in remaining tables, keep it
      if (tableName && tableName.trim() !== "" && tableNames.indexOf(tableName) > -1) {
        return true;
      }
      // If tableName is empty/undefined and colName matches a remaining calculated field alias, keep it
      if ((!tableName || tableName.trim() === "") && colName && calculatedFieldAliases.indexOf(colName) > -1) {
        return true;
      }
      return false;
    };

    // Filter whereData - keep if table exists OR if it references a remaining calculated field
    const whereData: IGenericSelectWhereDataModel[] = [];
    genericSelectData.whereData.forEach((oldData) => {
      if (oldData.type === EManagedDbUpdateWhereType.and) {
        if (shouldKeepField(oldData.andCondition!.tableName, oldData.andCondition!.colName)) {
          whereData.push(oldData);
        }
      } else {
        const orConditions = oldData.orConditions?.filter((x) => shouldKeepField(x.tableName, x.colName));
        if (orConditions && orConditions?.length > 0) {
          whereData.push({
            type: EManagedDbUpdateWhereType.orGroup,
            orConditions
          });
        }
      }
    });

    // Filter sortData - keep if table exists OR if it references a remaining calculated field
    const sortData = genericSelectData.sortData.filter((x) => shouldKeepField(x.tableName, x.colName));

    const result: IGenericSelectDataModel = {
      tables,
      fields: genericSelectData.fields.filter((x) => tableNames.indexOf(x.tableName) > -1),
      calculatedFields,
      relations,
      whereData,
      sortData
    };

    this.props.handleDelete(result);
    this.setState({
      isDeleteModalVisible: false,
      tableToDelete: null,
      areasToDelete: { tables: [], relations: [] }
    });
  };

  getSelectedColumnsText = (selectedColumns: IGenericSelectDataField[]) => {
    const columns = selectedColumns.map((x) => x.colName);
    // TODO! Add alias!
    if (!columns) return "";
    let columnText = columns?.slice(0, 3).join(", ");
    if (columnText?.length > 30) {
      columnText = columns?.slice(0, 2).join(", ");
      return columnText + (columns?.length > 2 ? `... ${columns.length - 2} more` : "");
    }
    return columnText + (columns?.length > 3 ? `... ${columns.length - 3} more` : "");
  };

  getTableActionMenu = (tableName: string, tableSchema: string, tableAlias?: string) => {
    return [
      {
        label: "Change source",
        icon: <KuikaSvg name="link" type="common" width={16} height={16} />,
        onClick: () => this.props.onSelectSourceClick()
      },
      {
        label: "Delete source",
        icon: <KuikaSvg name="trash" type="common" width={16} height={16} />,
        onClick: () => this.handleDeleteTable(tableSchema, tableName, tableAlias),
        danger: true
      }
    ];
  };

  renderTableCollapse = (table: IGenericSelectTablesModel) => {
    const targetTable = this.props.tables.find((x) => x.name === table.name);
    const targetTableColumns = targetTable?.columns ?? [];
    // Filter fields that match this specific table entry (by alias if exists, otherwise by name)
    const targetTableSelectedColumns = this.props.genericSelectData.fields.filter((x) => {
      if (table.alias) {
        return x.tableAlias === table.alias;
      }
      return x.tableName === targetTable?.name && !x.tableAlias;
    });
    // Display name: show alias if exists, otherwise schema.tableName
    const displayName = table.alias || (table.schema ? `${table.schema}.${table.name}` : table.name);

    return {
      key: table.alias || table.name,
      label: (
        <div className="kuika-flex-center--gap-xs" data-testid="selectActionSourceTable" title={displayName}>
          <KuikaSvg name="table" type="common" width={16} height={16} />
          {displayName}
        </div>
      ),
      extra: (
        <div className="kuika-flex-center--gap kuika-collapse-extra-hover">
          <KuikaTooltip content="Join">
            <KuikaSvg
              name="route-square-2"
              type="common"
              width={16}
              height={16}
              className="join-svg"
              pointer
              onClick={(e) => {
                e.stopPropagation();
                this.props.handleAddJoinClick(table.name, table.schema);
              }}
            />
          </KuikaTooltip>
          <div onClick={(e) => e.stopPropagation()}>
            <KuikaDropdownMenu options={this.getTableActionMenu(table.name, table.schema, table.alias)} trigger="click">
              {(open) => (
                <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
                  <KuikaSvg width={16} height={16} name="dots" />
                </KuikaButton>
              )}
            </KuikaDropdownMenu>
          </div>
        </div>
      ),
      className: "kuika_diagram_scheme kuika_generic_sql__table-collapse",
      styles: { header: { backgroundColor: this.getTableBackgroundColor(table.name) } },
      children: (
        <>
          {targetTableColumns
            ?.filter((col) => targetTableSelectedColumns.map((x) => x.colName)?.indexOf(col.name) > -1)
            .map((col, idx) => {
              const alias = targetTableSelectedColumns.find((x) => x.colName === col.name)?.alias;
              return (
                <KuikaMenuItem
                  key={col.name + idx}
                  className="child-label child-label-with-checkbox"
                  leftIcon={this.props.getTypeIcon(col.sqlDataType)}
                  title={
                    <KuikaText title={col.name}>
                      {col.name}
                      {alias && ` (${alias})`}
                    </KuikaText>
                  }
                  style={{ backgroundColor: this.getTableBackgroundColor(table.name) }}
                />
              );
            })}
        </>
      )
    };
  };

  render() {
    const selectData = this.props.genericSelectData;
    const collapseItems = selectData.tables.map((x) => this.renderTableCollapse(x));

    return (
      <>
        {this.state.isDeleteModalVisible && (
          <DeleteModal
            triggerModal={this.state.isDeleteModalVisible}
            deleteType="Source"
            itemName={this.state.tableToDelete || ""}
            extraText={(() => {
              // Tables that will be removed (not connected to origin after deletion)
              const tablesToRemove = this.state.areasToDelete.tables.filter((t) => t.areaIdx !== 0).map((t) => t.name);
              // Tables that will remain (connected to origin)
              const tablesToKeep = this.state.areasToDelete.tables.filter((t) => t.areaIdx === 0).map((t) => t.name);
              // Relations that will be removed
              const relationsToRemove = this.state.areasToDelete.relations.filter((r) => r.areaIdx !== 0).length;

              if (tablesToRemove.length > 0) {
                return (
                  <>
                    source? <b>{[this.state.tableToDelete, ...tablesToRemove].join(", ")}</b> tables will be removed.
                    {tablesToKeep.length > 0 && (
                      <>
                        {" "}
                        <b>{tablesToKeep.join(", ")}</b> will remain.
                      </>
                    )}
                  </>
                );
              }
              return <>source? This table will be removed.</>;
            })()}
            deleteItem={this.handleExecuteDelete}
            closeModal={() =>
              this.setState({
                isDeleteModalVisible: false,
                tableToDelete: null,
                areasToDelete: { tables: [], relations: [] }
              })
            }
          />
        )}
        {selectData.tables?.length > 0 ? (
          <KuikaCollapsePanel
            items={collapseItems}
            activeKey={this.props.expandedKeys}
            onChange={(keys) => this.props.onExpandedKeysChange(keys as string[])}
            expandIcon={({ isActive }) =>
              isActive ? (
                <KuikaSvg name="k-down" type="arrows" width={16} height={16} />
              ) : (
                <KuikaSvg name="k-right" type="arrows" width={16} height={16} />
              )
            }
          />
        ) : (
          <KuikaEmptyState
            customName="No source selected yet."
            description={
              <div>
                <KuikaSmallText
                  onClick={this.props.onSelectSourceClick}
                  className="kuika-text-blue-5 kuika-cursor-pointer"
                >
                  Select source{" "}
                </KuikaSmallText>
                <KuikaSmallText>to create complex queries.</KuikaSmallText>
              </div>
            }
            svgName="table"
          />
        )}
      </>
    );
  }
}

export const TableListComponent = TableList;
